import * as React from 'react';
import debounce from 'lodash.debounce';
import './SearchBar.scss';
import SearchModelProductsClass from 'src/model/searchModelProduct.model';
import { goToLogin } from 'src/utils/navigation';
import { isMobile } from 'src/utils/responsiveHelpers';
import env from 'src/shared/env';
import {
  startEndWithWhiteSpacesValidation,
  whiteSpacesValidation
} from 'src/utils/whiteSpacesValidation';
import i18n from 'src/translation/i18n';
import { analyticsListNames } from 'src/analytics/constant';

interface ISearchBarProps {
  match: any;
  searchText: string;
  results: SearchModelProductsClass;
  isLoggedIn: boolean;
  placeholder: string;
  handleOnSubmitSearch(searchText: string): void;
  handleOnSelectedPart(searchText: string): void;
  handleOnSelectedModel?(searchText: string, manufacturer: string): void;
  handleDoSearch(searchText: string, page?: number, onFinish?: any): void;
  handleSetText: (searchText: string) => void;
  location?: any;
  shouldShowItems?: boolean; // For use with search view where we don't want to show dropdown
  hideSearchButton?: boolean;
  searchLoading: boolean;
  hideModels?: boolean;
  hideViewAll?: boolean;
  resetSearchbar?: IResetSearchBar;
  setResetSearchbar?(value: IResetSearchBar): void;
  fireProductClickEvent?(data: any): void;
  fireProductImpressionEvent?(data: any): void;
  onResetPage: ()=> void;
}

export interface IResetSearchBar {
  update: boolean;
  value: boolean;
}

interface ISearchBarState {
  showItems: boolean;
  searchValue: string;
  isMobile: boolean;
}

export class SearchBar extends React.Component<
  ISearchBarProps,
  ISearchBarState
> {
  public searchDropdown: any;
  public input: any = null;

  constructor(props: any) {
    super(props);
    this.searchDropdown = React.createRef();
  }

  public state = {
    showItems: false,
    searchValue: this.props.searchText ? this.props.searchText : '',
    isMobile: isMobile(),
    resetSearchValue: false
  };

  public static defaultProps = {
    resetSearchbar: { update: false, value: false }
  };

  public static getDerivedStateFromProps(props: any, state: any) {
    if (props.resetSearchbar && props.resetSearchbar.value === true) {
      return { resetSearchValue: true };
    } else {
      return { resetSearchValue: false };
    }
  }
  public componentDidMount() {
    document.addEventListener('mousedown', this.handleClickOutside, false);
    window.addEventListener('resize', this.handleWindowSizeChange);
  }

  public componentWillUnmount() {
    document.removeEventListener('mousedown', this.handleClickOutside, false);
    window.removeEventListener('resize', this.handleWindowSizeChange);
  }

  public componentDidUpdate(prevProps: any, prevState: any) {
    if (prevState.resetSearchValue) {
      const reset = { ...this.props.resetSearchbar, value: false };
      this.input.value = '';
      this.setState({ searchValue: '' });
      this.props.setResetSearchbar(reset);
    }
  }

  public handleWindowSizeChange = () => {
    const isMob = isMobile();
    if (isMob !== this.state.isMobile) {
      this.setState({ isMobile: isMob });
    }
  };

  private handleClickOutside = (e: any) => {
    if (this.searchDropdown == null || this.searchDropdown.contains == null) {
      return;
    }
    if (this.searchDropdown.contains(e.target)) {
      // The click is inside, continue whit what we are doing
      return;
    }
    // The click is outside, hide dropdown
    this.setState({
      showItems: false
    });
  };

  private setShowItems = () => {
    if (this.props.shouldShowItems == null || this.props.shouldShowItems) {
      if (this.props.isLoggedIn) {
        this.setState({
          showItems: true
        });
      } else {
        goToLogin();
      }
    }
  };

  private validateSearchText = (text: string) => {
    let validate = true;
    if (text && text.match(/\s/g) && text.match(/\s/g).length === text.length) {
      validate = false;
    }

    return validate;
  };

  private handleOnChangeInputSearch = (e: any) => {
    this.props.onResetPage();
    const clearText = e.target.value;
    this.setState({
      searchValue: e.target.value
    });
    if (!this.validateSearchText(e.target.value)) {
      return;
    }

    this.props.handleDoSearch(clearText);

    if (clearText) {
      this.setState({ searchValue: clearText }, this.setShowItems);
    } else {
      this.setState({
        showItems: false,
        searchValue: clearText
      });
    }
  };

  public onSubmit = (e: any) => {
    e.preventDefault();
    this.props.onResetPage();
    if (this.validateSearchText(this.input.value)) {
      this.props.handleSetText(this.input.value);
    }
    if (
      !this.input.value ||
      (this.input.value.trim() === this.props.match.params.text &&
        !this.props.results.hasExactResult) ||
      whiteSpacesValidation(this.input.value) ||
      !this.validateSearchText(this.props.searchText)
    ) {
      this.input.value = '';
      this.setState({ searchValue: '' });
      return;
    }
    if (this.props.isLoggedIn) {
      this.props.handleDoSearch(this.input.value, null, () => {
        this.setState({ showItems: false });
        const hasExactResult = this.props.results.hasExactResult;
        if (hasExactResult) {
          this.props.handleOnSelectedPart(
            this.props.results.products[0].productNumber
          );
          this.input.value = '';
          this.setState({ searchValue: '' });
          return;
        }
        // Validate, if we just found one, go directly to the item
        const modelsFound = this.props.results.models.length;
        const productsFound = this.props.results.products.length;
        const totalFound = modelsFound + productsFound;
        if (totalFound === 1) {
          if (modelsFound) {
            this.props.handleOnSelectedModel(
              this.props.results.models[0].name,
              this.props.results.models[0].manufacturer
            );
            return;
          }
          if (productsFound) {
            this.props.handleOnSelectedPart(
              this.props.results.products[0].productNumber
            );
            return;
          }
        }
        let searchText = this.props.searchText;
        if (this.input) {
          // Force latest value on input
          searchText = this.input.value.trim();
          this.props.handleSetText(searchText);
        }
        this.props.handleOnSubmitSearch(searchText);
        this.input.value = '';
        this.setState({ searchValue: '' });
      });
    } else {
      goToLogin();
    }
  };

  private viewAll = () => {
    if (this.props.isLoggedIn) {
      this.setState({ showItems: false });
      let searchText = this.props.searchText;
      if (this.input) {
        // Force latest value on input
        searchText = this.input.value.trim();
        this.props.handleSetText(searchText);
      }
      this.props.handleOnSubmitSearch(searchText);
      this.props.fireProductImpressionEvent({
        list: analyticsListNames.SEARCH_RESULTS,
        listCriteria: searchText,
        listPage: 1,
        products: this.props.results.products
      });
    } else {
      goToLogin();
    }
  };

  public handleOnSelectedModel = (name: string, manufacturer: string) => {
    console.log('name', name);
    this.props.handleSetText(name);
    // Force set input
    if (this.input) {
      this.input.value = name;
    }
    this.setState({ showItems: false, searchValue: name });
    if (this.props.handleOnSelectedModel) {
      this.props.handleOnSelectedModel(name, manufacturer);
      const path =
        env.REACT_APP_BASE_ENVIRONMENT === 'CA'
          ? '/ca/en/content/'
          : '/us/content/';

      if (window.location.pathname === path && isMobile()) {
        location.reload();
      }
    }
  };

  public handleOnSelectedPart = (name: string) => {
    this.props.handleSetText(name);
    // Force set input
    if (this.input) {
      this.input.value = name;
    }
    this.setState({ showItems: false, searchValue: name });
    this.props.handleOnSelectedPart(name);
    this.fireProductClickEvent(name);
    this.setState({ searchValue: '' });
  };

  private fireProductClickEvent = (productNumber: string) => {
    const productClicked = this.props.results.products.find(
      product => product.productNumber === productNumber
    );
    const position = this.props.results.products.indexOf(productClicked) + 1;
    productClicked['position'] = position;
    const page = Math.floor((position - 1) / 10) + 1;
    this.props.fireProductClickEvent({
      product: productClicked,
      list: analyticsListNames.SEARCH_RESULTS,
      listPage: page
    });
  };

  public highlightText = (completeText: string) => {
    if (this.props.searchText == null) {
      return completeText;
    }
    if (completeText == null) {
      return '';
    }
    const searched = this.props.searchText.toUpperCase();
    const complete = completeText.toUpperCase();
    return complete.replace(
      searched,
      '<span class="highlight">' + searched + '</span>'
    );
  };

  private renderParts() {
    return (
      <React.Fragment>
        <p className="search-dropdown-total">
          Parts ( {this.props.results.productFound} )
        </p>
        {this.props.results.products &&
          this.props.results.products.map(
            product =>
              product.id !== '' && (
                <li
                  className="search-dropdown-item"
                  key={product.id}
                  onClick={() => {
                    this.handleOnSelectedPart(product.productNumber);
                  }}
                >
                  <p
                    className="name"
                    dangerouslySetInnerHTML={{
                      __html: this.highlightText(
                        `${product.manufacturer} ${product.name}`
                      )
                    }}
                  />
                  <p
                    className="description"
                    dangerouslySetInnerHTML={{
                      __html: this.highlightText(product.description)
                    }}
                  />
                </li>
              )
          )}
      </React.Fragment>
    );
  }

  private renderModels() {
    return (
      <React.Fragment>
        <p className="search-dropdown-total">
          Models ( {this.props.results.modelsFound} )
        </p>
        {this.props.results.models &&
          this.props.results.models.map(
            model =>
              model.id !== '' && (
                <li
                  className="search-dropdown-item search-dropdown-item-model"
                  key={model.id}
                  onClick={() =>
                    this.handleOnSelectedModel(model.name, model.manufacturer)
                  }
                >
                  <p
                    className="name"
                    dangerouslySetInnerHTML={{
                      __html: this.highlightText(
                        `${model.manufacturer} ${model.name}`
                      )
                    }}
                  />
                </li>
              )
          )}
      </React.Fragment>
    );
  }

  private renderViewAll() {
    return (
      <p className="search-dropdown-view-all" onClick={this.viewAll}>
        {i18n.t('recentOrders.viewAll')}
      </p>
    );
  }

  private renderSearchDropdown() {
    const { productFound, modelsFound } = this.props.results;
    // Only show if we already have results
    const shouldShowDropdown =
      this.state.showItems && (!!productFound || !!modelsFound);
    return (
      shouldShowDropdown && (
        <ul
          className={
            shouldShowDropdown
              ? 'search-dropdown'
              : 'search-dropdown hide-dropdown'
          }
          tabIndex={0}
          ref={searchDropdown => (this.searchDropdown = searchDropdown)}
        >
          {this.renderParts()}
          {!this.props.hideModels ? this.renderModels() : null}
          {!this.props.hideViewAll ? this.renderViewAll() : null}
        </ul>
      )
    );
  }

  public render() {
    const { hideSearchButton, placeholder } = this.props;
    return (
      <div className="rp-search-bar">
        <form onSubmit={this.onSubmit}>
          <div className="container-fluid rp-search-bar--container">
            <div className="row no-gutters">
              <div className="col-10 col-lg-9">
                <input aria-label="Search millions of parts and models"
                  ref={i => (this.input = i)}
                  className="search-input"
                  type="text"
                  onChange={this.handleOnChangeInputSearch}
                  placeholder={placeholder}
                  value={this.state.searchValue}
                />
                {this.renderSearchDropdown()}
              </div>
              {!hideSearchButton && (
                <div className="col-2 col-lg-3">
                  <button className="search-button" aria-label="Submit your search">
                    <span className="rpds rpds-search search-button-icon" />
                    {env.REACT_APP_BASE_ENVIRONMENT !== 'CA' ? (
                      <span className="d-none d-lg-inline rp-search-bar--text">
                        {i18n.t('common.search')}
                      </span>
                    ) : (
                      ''
                    )}
                  </button>
                </div>
              )}
            </div>
          </div>
        </form>
      </div>
    );
  }
}
export default SearchBar;
